import React from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { Rectangle, PieChart, Pie, Cell, ResponsiveContainer, Tooltip, ComposedChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import './IndicadoresVisuals.css';

export function IndicatorTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((item, i, all) => item.value != null && all.findIndex(other => other.dataKey === item.dataKey && other.name === item.name) === i);
  return <Box className="indicator-tooltip">
    {label != null && <Typography className="indicator-tooltip-title">{label}</Typography>}
    {rows.map((item, i) => <Box className="indicator-tooltip-row" key={i}>
      <span style={{background:item.payload?.color || item.color || '#548EE6'}}/>
      <Typography>{item.name}</Typography>
      <strong>{typeof item.value === 'number' ? item.value.toLocaleString('es-PE',{maximumFractionDigits:2}) : item.value}</strong>
    </Box>)}
  </Box>;
}

export function IndicatorDonut({ data }) {
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)');
  const values = data.map(item => ({...item,value:Math.max(0,Number(item.value)||0)}));
  const total = values.reduce((sum,item)=>sum+item.value,0);
  return <Box className="indicator-composition">
    <Box className="indicator-donut" aria-label={`${total.toLocaleString('es-PE')} medicamentos clasificados`}>
      {total > 0 ? <ResponsiveContainer width="100%" height="100%">
        <PieChart accessibilityLayer>
          <Pie data={values} dataKey="value" nameKey="name" innerRadius="70%" outerRadius="92%" startAngle={90} endAngle={-270}
            paddingAngle={total ? 2 : 0} cornerRadius={5} stroke="#FFFFFF" strokeWidth={3}
            isAnimationActive={!reduced} animationDuration={850} animationEasing="ease-out">
            {values.map(item=><Cell key={item.name} fill={item.color}/>)}
          </Pie>
          <Tooltip content={<IndicatorTooltip/>} animationDuration={0}/>
        </PieChart>
      </ResponsiveContainer> : <Box className="indicator-ring-empty"/>}
      <Box className="indicator-donut-center"><strong>{total.toLocaleString('es-PE')}</strong><span>{total ? 'Clasificados' : 'Sin datos'}</span></Box>
    </Box>
    <Box component="ul" className="indicator-donut-legend">
      {values.map(item=><li key={item.name}><span className="indicator-swatch" style={{background:item.color}}/><span>{item.name}<small>{total ? (item.value/total*100).toFixed(1) : '0'}%</small></span><strong>{item.value.toLocaleString('es-PE')}</strong></li>)}
    </Box>
  </Box>;
}

export function IndicatorRanking({ data, dataKey, color, unit }) {
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)');
  const mobile = useMediaQuery('(max-width:599px)');
  return <Box className="indicator-ranking-scroll"><Box sx={{height:Math.max(230,data.length*48),minWidth:0}}>
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} layout="vertical" margin={{top:8,right:22,bottom:8,left:0}} accessibilityLayer>
        <CartesianGrid vertical={false} stroke="#EDF1F6" strokeDasharray="3 5"/>
        <XAxis type="number" tickLine={false} axisLine={false} tick={{fill:'#7B8798',fontSize:11}}/>
        <YAxis type="category" dataKey="producto" width={mobile?115:175} tickLine={false} axisLine={false}
          tick={{fill:'#627087',fontSize:11}} tickFormatter={v=>v.length>(mobile?17:26)?v.slice(0,mobile?17:26)+'…':v}/>
        <Tooltip content={<IndicatorTooltip/>} cursor={{fill:'#F4F7FB'}} animationDuration={0} labelFormatter={(_,p)=>p?.[0]?.payload?.producto}/>
        <Bar dataKey={dataKey} name={unit} fill={color} radius={[0,6,6,0]} maxBarSize={18}
          isAnimationActive={!reduced} animationDuration={850} animationEasing="ease-out"/>
      </ComposedChart>
    </ResponsiveContainer>
  </Box></Box>;
}

// Round only the exposed end of a stack, including months with zero in the last series.
export function IndicatorStackShape({ field, horizontal=false, ...props }) {
  const order = ['sobrestock','normostock','substock','desabastecido'];
  const last = order.filter(key => Number(props.payload?.[key]) > 0).pop();
  return <Rectangle {...props} radius={field === last ? (horizontal ? [0,6,6,0] : [6,6,0,0]) : 0}/>;
}
